// docs/117 TT6: when the Thing Below's arms come up. Every arm the board draws that was not up on
// the board drawn before it rises (the first board that shows them, and every later surfacing);
// an arm the board now shows as gone under (`onBoard: false`) sinks from where it last stood. An
// arm the fog hides is simply forgotten, never sunk (that would show where it is). Pure, so the
// scene's effects (game/board/TentacleFx.ts) and the tests read the same rules.
import { creatureArt, type CreatureGround } from '../../game/creatureArt';

/** One arm's emergence: the darkening and churn, the burst, the rise (ms at playing speed 1). */
export const EMERGE = {
  /** The water darkens and churns, bubbles boil up (the sand cracks and trickles). */
  churnMs: 700,
  /** It bursts up out of the water or the sand and uncurls. */
  riseMs: 620,
  /** Spray falls back and sand pours off it after it stands. */
  settleMs: 650,
  /** Each arm after the first in one surfacing comes up this much later. */
  staggerMs: 320,
} as const;

/** What the scene knows of a unit to decide whether it rose or sank. */
export interface ArmSeen { unitId: number; appearance?: number; onBoard?: boolean; downed?: boolean }

/** Whether a unit is one of the Thing Below's arms. */
export function isArm(u: { appearance?: number }): boolean {
  return creatureArt(u.appearance)?.kind === 'tentacle';
}

/** What an arm comes up through: its pool, or the desert sand. */
export function armGround(u: { appearance?: number }): CreatureGround | undefined {
  const a = creatureArt(u.appearance);
  return a?.kind === 'tentacle' ? a.ground : undefined;
}

export interface Surfacing {
  /** Arms that rise now, each with its delay in the stagger (by unit id). */
  rise: Array<{ unitId: number; delayMs: number }>;
  /** Arms that went under since the last board. */
  sink: number[];
  /** The arms up after this board. */
  up: Set<number>;
}

/**
 * The arms that rise and sink as a board with `units` is drawn after one where `up` were up. A
 * downed arm neither rises nor sinks (it falls as anything does); one absent from `units` (hidden
 * by the fog) is dropped from `up` quietly.
 */
export function surfacing(up: ReadonlySet<number>, units: readonly ArmSeen[], staggerMs: number = EMERGE.staggerMs): Surfacing {
  const arms = units.filter(isArm);
  const standing = arms.filter((u) => u.onBoard !== false && !u.downed).map((u) => u.unitId).sort((a, b) => a - b);
  const rising = standing.filter((id) => !up.has(id));
  const sink = arms.filter((u) => u.onBoard === false && up.has(u.unitId)).map((u) => u.unitId).sort((a, b) => a - b);
  return {
    rise: rising.map((unitId, i) => ({ unitId, delayMs: i * staggerMs })),
    sink,
    up: new Set(standing),
  };
}

/** An arm's idle sway: degrees either way and the period, a little different for each arm. */
export function swayOf(unitId: number): { deg: number; ms: number; phase: number } {
  const h = Math.imul(unitId + 0x9e37, 0x85eb) >>> 0;
  return { deg: 2.5 + (h % 3) * 0.75, ms: 1300 + (h % 7) * 110, phase: (h >>> 4) % 1000 / 1000 };
}

/**
 * How far an arm has come up out of the water at `k` (0 under, 1 standing) of its rise, as the
 * rows of its 24 px image that show above the waterline (`shown`) and how far the image is pushed
 * down so those rows end at the waterline (`sunk`); `squash` is its width while it uncurls.
 * `k` may overshoot past 1 (an eased rise): it shows whole and stands a touch above, then settles.
 */
export function riseFrame(k: number, rows = 24): { shown: number; sunk: number; stretch: number; squash: number } {
  const c = Math.max(0, Math.min(1, k));
  const shown = Math.round(rows * c);
  return { shown, sunk: Math.round(rows * (1 - k)), stretch: 1 + 0.18 * (1 - c), squash: 0.7 + 0.3 * c };
}
